import { Component, Input } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export interface ActionModalConfig {
  title: string;
  message?: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
  showSecondaryButton?: boolean;
  actionType: 'excel-download' | 'confirm-approval';
}

@Component({
  selector: 'app-excel-download-modal',
  templateUrl: './excel-download-modal.component.html',
  styleUrls: ['./excel-download-modal.component.scss'],
})
export class ExcelDownloadModalComponent {

  @Input() data: any[] = [];
  @Input() fileName = 'Download.xlsx';
  @Input() sheetName = 'Sheet1';
  @Input() headers: { [key: string]: string } = {};
  @Input() editableColumns: string[] = [];
  @Input() config
  isDownloading = false;


  constructor(private modalController: ModalController, private toastController: ToastController) { }

  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }


  handlePrimaryAction() {
    if (this.isDownloading) {
      return;
    }
    if (this.config.actionType === 'excel-download') {
      this.downloadExcel();
    } else if (this.config.actionType === 'confirm-approval') {
      this.confirmApproval();
    }
    else if (this.config.actionType === 'confirm-replace') {
      this.confirmReplace();
    }
  }

  confirmReplace() {
    this.modalController.dismiss(true, 'confirm-replace');
  }

  async downloadExcel() {
    try {
      this.isDownloading = true;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(this.sheetName);

      const headerKeys = Object.keys(this.headers);
      const headerLabels = headerKeys.map(k => this.headers[k]);

      /* Header row */
      const headerRow = worksheet.addRow(headerLabels);
      headerRow.eachCell((cell, index) => {
        const label = headerLabels[index - 1];
        const isEditable = this.editableColumns.includes(label);
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: isEditable ? 'FFFFFF00' : 'FFD9E1F2' }
        };
        cell.alignment = {
          horizontal: 'left',
          vertical: 'middle'
        };
        //cell.protection = { locked: true };
      });

      /* Data rows */

      this.data.forEach(row => {
        const values = headerKeys.map(key => row[key] ?? '');
        const dataRow = worksheet.addRow(values);

        headerLabels.forEach((label, i) => {
          const cell = dataRow.getCell(i + 1);
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
          cell.protection = { locked: !this.editableColumns.includes(label) };
        });
      });

      /* ✅ AUTO-FIT */
      worksheet.columns.forEach(column => {
        if (!column) return;

        let maxLength = 8;

        column.eachCell({ includeEmpty: true }, cell => {
          const value = cell.value ? String(cell.value) : '';
          maxLength = Math.max(maxLength, value.length);
        });

        column.width = Math.min(maxLength + 3, 45);
      });

      // await worksheet.protect('', {
      //   selectLockedCells: true,
      //   selectUnlockedCells: true,
      //   formatCells: false,
      //   formatColumns: true,
      //   formatRows: false,
      //   insertRows: false,
      //   deleteRows: false
      // });

      const buffer = await workbook.xlsx.writeBuffer();
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, '0');
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const yy = String(today.getFullYear()).slice(-2);
      const min = String(today.getMinutes()).padStart(2, '0');
      const sec = String(today.getSeconds()).padStart(2, '0');

      const downloadFileName = `ReassignmentRecords_${dd}_${mm}_${yy}.xlsx`;

      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        downloadFileName
      );

      const toast = await this.toastController.create({
        message: 'Excel downloaded successfully',
        duration: 3000,
        position: 'bottom',
        cssClass: 'brand-toast'
      });
      await toast.present();

      this.modalController.dismiss(true, 'excel-download');
    }
    finally {
      this.isDownloading = false;
    }
  }

  confirmApproval() {
    this.modalController.dismiss(true, 'confirm-approval');
  }


}
